<template>
  <div class="data-sync-container">
    <!-- 同步配置列表 -->
    <canvas-glass-card title="ERP数据拉取" variant="light" :hoverable="false" class="table-card">
      <template #header>
        <a-row>
          <a-col flex="auto">
            <span class="glass-card__title">ERP数据拉取</span>
          </a-col>
          <a-col flex="200px">
            <a-row justify="space-between" :wrap="false">
              <a-button type="primary" @click="syncAll" :loading="syncAllLoading">
                <template #icon>
                  <icon-download />
                </template>
                拉取所有数据
              </a-button>
              <a-button type="primary" status="success" @click="handleAutoGenerateWorkOrders" :loading="autoGenerateLoading">
                <template #icon>
                  <icon-plus />
                </template>
                自动生成工单
              </a-button>
            </a-row>
          </a-col>
        </a-row>
      </template>

      <!-- 同步配置表格 -->
      <a-table
        row-key="tableName"
        :columns="tableColumns"
        :data="syncConfigList"
        :pagination="false"
        :loading="loading"
        column-resizable
        stripe
        :bordered="{cell:true}"
      >
        <template #action="{ record }">
          <a-space>
            <a-button type="text" size="mini" status="success" @click="syncSingle(record.tableName)" 
                      class="button-table-option" :loading="syncSingleLoading[record.tableName]">
              <template #icon>
                <icon-download />
              </template>
              拉取
            </a-button>
          </a-space>
        </template>
      </a-table>
    </canvas-glass-card>

    <!-- 同步结果展示 -->
    <canvas-glass-card v-if="syncResult" title="拉取结果" variant="light" :hoverable="false" class="result-card">
      <a-alert type="info">
        <template #title>拉取结果</template>
        <div style="white-space: pre-wrap;">{{ syncResult }}</div>
      </a-alert>
    </canvas-glass-card>
    
    <!-- 同步进度蒙版 -->
    <div v-if="syncAllLoading || Object.values(syncSingleLoading).some(v => v)" class="sync-overlay">
      <div class="sync-spinner">
        <a-spin :tip="syncTip" class="sync-spin">
          <template #element>
            <a-spin-dot />
          </template>
        </a-spin>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { Message } from '@arco-design/web-vue'
import CanvasGlassCard from '@/components/CanvasGlassCard.vue'
import { 
  IconDownload,
  IconPlus
} from '@arco-design/web-vue/es/icon'
import { 
  getSyncConfigList, 
  syncSingleTable, 
  syncAllTables,
  autoGenerateWorkOrders
} from '@/apis/system/data-sync'

// 同步配置列表
const syncConfigList = ref([])
// 加载状态
const loading = ref(false)
// 同步所有表的加载状态
const syncAllLoading = ref(false)
// 自动工单生成的加载状态
const autoGenerateLoading = ref(false)
// 同步单个表的加载状态
const syncSingleLoading = ref({})
// 同步提示信息
const syncTip = ref('')
// 同步结果
const syncResult = ref('')

// 表格列定义
const tableColumns = ref([
  { title: '同步项', dataIndex: 'name', width: 200, ellipsis: true, tooltip: true },
  { title: '表名', dataIndex: 'tableName', width: 200, ellipsis: true, tooltip: true },
  { title: '操作', dataIndex: 'action', width: 120, slotName: 'action', align: 'center', fixed: 'right' }
])

// 获取同步配置列表
const fetchSyncConfigList = async () => {
  try {
    loading.value = true
    const response = await getSyncConfigList()
    if (response.code === 0) {
      syncConfigList.value = response.data
      // 初始化单个同步加载状态
      syncSingleLoading.value = {}
      response.data.forEach(item => {
        syncSingleLoading.value[item.tableName] = false
      })
    } else {
      Message.error(response.message || '获取同步配置列表失败')
    }
  } catch (error) {
    Message.error('获取同步配置列表失败: ' + error.message)
    console.error('获取同步配置列表失败:', error)
  } finally {
    loading.value = false
  }
}

// 拉取单个表
const syncSingle = async (tableName) => {
  try {
    syncSingleLoading.value[tableName] = true
    syncTip.value = `正在拉取表: ${tableName}...`
    
    const response = await syncSingleTable(tableName)
    if (response.code === 0) {
      Message.success(`[${tableName}] 拉取成功`)
      syncResult.value = response.data
    } else {
      Message.error(response.message || `[${tableName}] 拉取失败`)
      syncResult.value = response.message
    }
  } catch (error) {
    Message.error(`[${tableName}] 拉取失败: ` + error.message)
    syncResult.value = `[${tableName}] 拉取失败: ` + error.message
    console.error(`[${tableName}] 拉取失败:`, error)
  } finally {
    syncSingleLoading.value[tableName] = false
    syncTip.value = ''
  }
}

// 拉取所有表
const syncAll = async () => {
  try {
    syncAllLoading.value = true
    syncTip.value = '正在拉取所有表，请稍候...'
    
    const response = await syncAllTables()
    if (response.code === 0) {
      Message.success('所有表拉取完成')
      syncResult.value = response.data
    } else {
      Message.error(response.message || '拉取失败')
      syncResult.value = response.message
    }
  } catch (error) {
    Message.error('拉取失败: ' + error.message)
    syncResult.value = '拉取失败: ' + error.message
    console.error('拉取失败:', error)
  } finally {
    syncAllLoading.value = false
    syncTip.value = ''
  }
}

// 自动生成工单
const handleAutoGenerateWorkOrders = async () => {
  try {
    autoGenerateLoading.value = true
    syncTip.value = '正在自动生成工单，请稍候...'
    
    const response = await autoGenerateWorkOrders()
    if (response.code === 0) {
      Message.success('工单自动生成完成')
      syncResult.value = response.data
    } else {
      Message.error(response.message || '工单自动生成失败')
      syncResult.value = response.message
    }
  } catch (error) {
    Message.error('工单自动生成失败: ' + error.message)
    syncResult.value = '工单自动生成失败: ' + error.message
    console.error('工单自动生成失败:', error)
  } finally {
    autoGenerateLoading.value = false
    syncTip.value = ''
  }
}

// 组件挂载时获取数据
onMounted(() => {
  fetchSyncConfigList()
})
</script>

<style scoped>
.data-sync-container {
  padding: 20px;
  position: relative;
}

.glass-card__title {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  line-height: 1.4;
}

.button-table-option {
  border: none;
  background: none;
  padding: 0;
  cursor: pointer;
}

.result-card {
  margin-top: 20px;
}

.sync-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
}

.sync-spinner {
  text-align: center;
}

.sync-spin:deep(.arco-spin-tip) {
  margin-top: 12px;
  font-size: 16px;
  color: #2c3e50;
}
</style>